import React, { useEffect } from "react";
import { Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { getSellerDetail } from "../../../actions/vendorActions";

const AVATAR_COLORS = ["#117a44", "#0d6efd", "#c8880a", "#6f42c1", "#0dcaf0", "#dc3545", "#20c997"];
const getAvatarColor = (name = "") => AVATAR_COLORS[(name || "").charCodeAt(0) % AVATAR_COLORS.length];
const getInitials = (name = "") =>
  name.split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";

const SellerEditForm = ({ match }) => {
  const sellerID = match.params.id;
  const { seller, loading } = useSelector((state) => state.sellers);
  const dispatch = useDispatch();
  const history = useHistory();

  useEffect(() => {
    dispatch(getSellerDetail(sellerID));
  }, []);

  const verified   = seller?.sellerVerification === "VERIFIED";
  const active     = seller?.isActive === true || seller?.isActive === "true";
  const name       = seller?.businessName || "—";
  const avatarColor = getAvatarColor(name);

  return (
    <>
      <style>{`
        .sef-page { background: #f4f6f9; min-height: 100vh; padding: 28px 24px; font-family: "Segoe UI", system-ui, sans-serif; }

        /* Hero header card */
        .sef-hero {
          background: linear-gradient(135deg, #1a2a3a 0%, #243447 100%);
          border-radius: 12px; padding: 24px 28px; margin-bottom: 24px;
          display: flex; align-items: center; gap: 20px; flex-wrap: wrap;
          box-shadow: 0 4px 16px rgba(0,0,0,0.14);
        }
        .sef-hero-avatar {
          width: 64px; height: 64px; border-radius: 14px;
          display: flex; align-items: center; justify-content: center;
          font-size: 1.5rem; font-weight: 800; color: #fff; flex-shrink: 0;
          box-shadow: 0 2px 8px rgba(0,0,0,0.25);
        }
        .sef-hero-info { flex: 1; min-width: 0; }
        .sef-hero-name { color: #fff; font-size: 1.35rem; font-weight: 800; margin: 0 0 4px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .sef-hero-sub  { color: #8aaccc; font-size: 0.8rem; margin: 0; }
        .sef-hero-badges { display: flex; gap: 8px; flex-wrap: wrap; margin-top: 8px; }
        .sef-badge {
          display: inline-flex; align-items: center; gap: 5px;
          padding: 4px 12px; border-radius: 20px;
          font-size: 0.72rem; font-weight: 700; letter-spacing: 0.03em;
        }
        .sef-back-btn {
          display: inline-flex; align-items: center; gap: 7px;
          background: rgba(255,255,255,0.1); color: #fff;
          border: 1px solid rgba(255,255,255,0.2);
          padding: 8px 16px; border-radius: 8px; font-size: 0.82rem;
          font-weight: 600; cursor: pointer; transition: background 0.15s;
          text-decoration: none; white-space: nowrap; flex-shrink: 0;
        }
        .sef-back-btn:hover { background: rgba(255,255,255,0.2); color: #fff; }

        /* Section panel */
        .sef-panel { background: #fff; border-radius: 10px; box-shadow: 0 1px 6px rgba(0,0,0,0.07); overflow: hidden; margin-bottom: 20px; }
        .sef-panel-header {
          padding: 13px 20px; border-bottom: 1px solid #f0f2f5;
          display: flex; align-items: center; gap: 10px;
          background: #fafbfc;
        }
        .sef-panel-icon { width: 30px; height: 30px; border-radius: 8px; display: flex; align-items: center; justify-content: center; font-size: 13px; flex-shrink: 0; }
        .sef-panel-title { font-size: 0.82rem; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: #6b7c93; margin: 0; }
        .sef-panel-body { padding: 20px; }

        /* Field grid */
        .sef-field-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; }
        .sef-field { display: flex; flex-direction: column; gap: 5px; }
        .sef-field-label { font-size: 0.68rem; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: #9aacbf; }
        .sef-field-value {
          font-size: 0.88rem; font-weight: 600; color: #1a2a3a;
          background: #f7f9fc; border: 1.5px solid #e9ecef;
          border-radius: 8px; padding: 9px 14px;
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        }
        .sef-field-value.empty { color: #b0bec5; font-style: italic; font-weight: 400; }
        .sef-field-value.full-width { grid-column: 1 / -1; white-space: normal; }

        /* Responsive */
        @media (max-width: 600px) {
          .sef-page { padding: 16px 12px; }
          .sef-hero { padding: 18px 16px; gap: 14px; }
          .sef-hero-avatar { width: 50px; height: 50px; font-size: 1.2rem; }
          .sef-hero-name { font-size: 1.1rem; }
          .sef-field-grid { grid-template-columns: 1fr; }
        }
      `}</style>

      <div className="sef-page">
        {loading ? (
          <Spinner animation="grow" variant="success"
            style={{ position: "fixed", top: "50%", left: "50%", zIndex: 9999 }}
          />
        ) : (
          <>
            {/* ── Hero header ── */}
            <div className="sef-hero">
              <div className="sef-hero-avatar" style={{ background: avatarColor }}>
                {getInitials(name)}
              </div>
              <div className="sef-hero-info">
                <div className="sef-hero-name">{name}</div>
                <div className="sef-hero-sub">{seller?.email || "No email on record"}</div>
                <div className="sef-hero-badges">
                  <span className="sef-badge" style={{
                    background: verified ? "#e8f5e9" : "#fff3e0",
                    color: verified ? "#2e7d32" : "#e65100",
                  }}>
                    <i className={`fas ${verified ? "fa-check-circle" : "fa-clock"}`} style={{ fontSize: 9 }} />
                    {verified ? "Verified" : "Unverified"}
                  </span>
                  <span className="sef-badge" style={{
                    background: active ? "#e8f5e9" : "#fce4ec",
                    color: active ? "#2e7d32" : "#c62828",
                  }}>
                    <i className={`fas ${active ? "fa-toggle-on" : "fa-toggle-off"}`} style={{ fontSize: 9 }} />
                    {active ? "Active" : "Inactive"}
                  </span>
                  {seller?.userType && (
                    <span className="sef-badge" style={{ background: "#e3f2fd", color: "#1565c0" }}>
                      <i className="fas fa-tag" style={{ fontSize: 9 }} />
                      {seller.userType}
                    </span>
                  )}
                  {seller?.operationMode && (
                    <span className="sef-badge" style={{ background: "#ede7f6", color: "#4527a0" }}>
                      <i className="fas fa-store" style={{ fontSize: 9 }} />
                      {seller.operationMode}
                    </span>
                  )}
                </div>
              </div>
              <button className="sef-back-btn" onClick={() => history.goBack()}>
                <i className="fas fa-arrow-left" />
                Back
              </button>
            </div>

            {/* ── Business Info ── */}
            <div className="sef-panel">
              <div className="sef-panel-header">
                <div className="sef-panel-icon" style={{ background: "#e3f2fd" }}>
                  <i className="fas fa-store" style={{ color: "#1565c0" }} />
                </div>
                <span className="sef-panel-title">Business Information</span>
              </div>
              <div className="sef-panel-body">
                <div className="sef-field-grid">
                  <div className="sef-field">
                    <span className="sef-field-label">Shop Name</span>
                    <span className={`sef-field-value${!seller?.businessName ? " empty" : ""}`}>
                      {seller?.businessName || "Not provided"}
                    </span>
                  </div>
                  <div className="sef-field">
                    <span className="sef-field-label">Operation Mode</span>
                    <span className={`sef-field-value${!seller?.operationMode ? " empty" : ""}`}>
                      {seller?.operationMode || "Not provided"}
                    </span>
                  </div>
                  <div className="sef-field">
                    <span className="sef-field-label">Business TIN</span>
                    <span className={`sef-field-value${!seller?.businessTIN ? " empty" : ""}`}
                      style={{ fontFamily: "SFMono-Regular, Consolas, monospace", fontSize: "0.82rem" }}>
                      {seller?.businessTIN || "Not provided"}
                    </span>
                  </div>
                  <div className="sef-field">
                    <span className="sef-field-label">Type of Goods</span>
                    <span className={`sef-field-value${!seller?.typeOfGoods ? " empty" : ""}`}>
                      {seller?.typeOfGoods || "Not provided"}
                    </span>
                  </div>
                  <div className="sef-field" style={{ gridColumn: "1 / -1" }}>
                    <span className="sef-field-label">Shop Address</span>
                    <span className={`sef-field-value${!seller?.businessAddress ? " empty" : ""}`}
                      style={{ whiteSpace: "normal" }}>
                      {seller?.businessAddress || "Not provided"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Contact Info ── */}
            <div className="sef-panel">
              <div className="sef-panel-header">
                <div className="sef-panel-icon" style={{ background: "#e8f5e9" }}>
                  <i className="fas fa-address-card" style={{ color: "#2e7d32" }} />
                </div>
                <span className="sef-panel-title">Contact Details</span>
              </div>
              <div className="sef-panel-body">
                <div className="sef-field-grid">
                  <div className="sef-field">
                    <span className="sef-field-label">Email Address</span>
                    <span className={`sef-field-value${!seller?.email ? " empty" : ""}`}>
                      {seller?.email || "Not provided"}
                    </span>
                  </div>
                  <div className="sef-field">
                    <span className="sef-field-label">Phone Number</span>
                    <span className={`sef-field-value${!seller?.businessPhone ? " empty" : ""}`}>
                      {seller?.businessPhone || "Not provided"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Account Info ── */}
            <div className="sef-panel">
              <div className="sef-panel-header">
                <div className="sef-panel-icon" style={{ background: "#ede7f6" }}>
                  <i className="fas fa-user-shield" style={{ color: "#4527a0" }} />
                </div>
                <span className="sef-panel-title">Account & Status</span>
              </div>
              <div className="sef-panel-body">
                <div className="sef-field-grid">
                  <div className="sef-field">
                    <span className="sef-field-label">Account Role</span>
                    <span className={`sef-field-value${!seller?.user_role ? " empty" : ""}`}>
                      {seller?.user_role || "Not assigned"}
                    </span>
                  </div>
                  <div className="sef-field">
                    <span className="sef-field-label">Account Type</span>
                    <span className={`sef-field-value${!seller?.userType ? " empty" : ""}`}>
                      {seller?.userType || "Not assigned"}
                    </span>
                  </div>
                  <div className="sef-field">
                    <span className="sef-field-label">Verification Status</span>
                    <span className="sef-field-value" style={{
                      color: verified ? "#2e7d32" : "#e65100",
                      background: verified ? "#e8f5e9" : "#fff3e0",
                      border: `1.5px solid ${verified ? "#c8e6c9" : "#ffe0b2"}`,
                    }}>
                      <i className={`fas ${verified ? "fa-check-circle" : "fa-clock"}`} style={{ marginRight: 6 }} />
                      {verified ? "Verified" : "Pending Verification"}
                    </span>
                  </div>
                  <div className="sef-field">
                    <span className="sef-field-label">Activation Status</span>
                    <span className="sef-field-value" style={{
                      color: active ? "#2e7d32" : "#c62828",
                      background: active ? "#e8f5e9" : "#fce4ec",
                      border: `1.5px solid ${active ? "#c8e6c9" : "#f5c6cb"}`,
                    }}>
                      <i className={`fas ${active ? "fa-toggle-on" : "fa-toggle-off"}`} style={{ marginRight: 6 }} />
                      {active ? "Active" : "Inactive"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default SellerEditForm;
